import { ValidationError, handleError } from "../../../../errors";
import { generateCodeChallenge, generateCodeVerifier, generateOAuthState } from "./pkce";
import type { AtoTokenResponse } from "./types";

const APS_AUTH_BASE = "https://developer.api.autodesk.com/authentication/v2";

const SS_VERIFIER = "atoPkceVerifier";
const SS_STATE = "atoOAuthState";

/** `data:read` alone covers Data Management (hubs/projects) and the Takeoff API; no admin scope is needed. */
const DEFAULT_SCOPES = "data:read";

/** APS public client (Desktop/Mobile/SPA + PKCE). Not a secret; safe in the client bundle. */
const APS_CLIENT_ID = "PCJDHGSaHjSJZTGYxuQJqgkjFSt6IL37iXFZbqq2f0GLtbSx";

/**
 * Tokens are held in memory only (never localStorage/disk) per APS public-client
 * guidance. They do not survive a task-pane reload; the user re-authenticates then.
 */
const tokenStore: { access?: string; refresh?: string; expiresAt?: number } = {};

/** Must match a callback URL registered on the APS app for this add-in origin. */
export function getAtoRedirectUri(): string {
  return `${window.location.origin}/ato-redirect.html`;
}

let refreshInFlight: Promise<boolean> | null = null;

export function clearAtoTokens(): void {
  tokenStore.access = undefined;
  tokenStore.refresh = undefined;
  tokenStore.expiresAt = undefined;
}

export function hasAtoRefreshToken(): boolean {
  return !!tokenStore.refresh?.trim();
}

export function isAtoAccessTokenExpired(): boolean {
  const expiresAt = tokenStore.expiresAt;
  if (expiresAt == null || !Number.isFinite(expiresAt)) return true;
  return Date.now() >= expiresAt;
}

function persistTokenResponse(data: AtoTokenResponse): void {
  tokenStore.access = data.access_token;
  if (data.refresh_token) {
    tokenStore.refresh = data.refresh_token;
  }
  const skewMs = 60_000;
  tokenStore.expiresAt = Date.now() + Math.max(0, (data.expires_in ?? 3600) * 1000 - skewMs);
}

async function postToken(body: Record<string, string>): Promise<AtoTokenResponse> {
  const params = new URLSearchParams({ ...body, client_id: APS_CLIENT_ID });
  const res = await fetch(`${APS_AUTH_BASE}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });
  if (!res.ok) {
    let apiMessage = "Token request failed";
    try {
      const j = (await res.json()) as { error_description?: string; error?: string };
      if (j.error_description || j.error) apiMessage = j.error_description || j.error || apiMessage;
    } catch {
      // ignore
    }
    throw handleError(Object.assign(new Error(apiMessage), { statusCode: res.status }), { status: res.status });
  }
  return (await res.json()) as AtoTokenResponse;
}

export async function exchangeAuthorizationCode(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_STATE);
  if (!verifier) {
    throw new ValidationError("Missing PKCE verifier; restart sign-in");
  }
  const data = await postToken({
    grant_type: "authorization_code",
    code,
    redirect_uri: getAtoRedirectUri(),
    code_verifier: verifier,
  });
  persistTokenResponse(data);
}

async function doRefresh(): Promise<boolean> {
  const refresh = tokenStore.refresh?.trim();
  if (!refresh) return false;
  try {
    const data = await postToken({
      grant_type: "refresh_token",
      refresh_token: refresh,
    });
    persistTokenResponse(data);
    return true;
  } catch {
    // Refresh failures are treated as session expiry — caller handles re-login via the normal auth flow.
    clearAtoTokens();
    return false;
  }
}

export async function refreshAtoAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Returns a usable access token, refreshing once if expired. */
export async function getValidAtoAccessToken(): Promise<string | null> {
  if (!isAtoAccessTokenExpired()) {
    const t = tokenStore.access?.trim();
    if (t) return t;
  }

  const ok = await refreshAtoAccessToken();
  if (!ok) return null;
  return tokenStore.access?.trim() ?? null;
}

export function peekAtoAccessToken(): string | null {
  return tokenStore.access?.trim() ?? null;
}

export async function buildAuthorizeUrl(): Promise<{ authorizeUrl: string; state: string }> {
  const verifier = generateCodeVerifier();
  const state = generateOAuthState();
  sessionStorage.setItem(SS_VERIFIER, verifier);
  sessionStorage.setItem(SS_STATE, state);

  const challenge = await generateCodeChallenge(verifier);
  const redirectUri = getAtoRedirectUri();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: APS_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: DEFAULT_SCOPES,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return {
    authorizeUrl: `${APS_AUTH_BASE}/authorize?${params.toString()}`,
    state,
  };
}

export function validateOAuthState(returnedState: string | null): boolean {
  const expected = sessionStorage.getItem(SS_STATE);
  if (!expected || !returnedState) return false;
  return expected === returnedState;
}

export function clearOAuthSessionMarkers(): void {
  sessionStorage.removeItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_STATE);
}
